import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Message {
  id: string;
  type: 'user' | 'agent';
  content: string;
  timestamp: Date;
  pdf_page?: number;
  view_context?: {
    tab?: 'pdf' | 'youtube' | 'notes' | 'browser' | 'screen';
    pdf_page?: number;
    selection?: string;
    youtube?: { video_id: string; t_sec: number };
    screen_share?: boolean;
  };
}

interface PersistedMessage {
  id: string;
  type: 'user' | 'agent';
  content: string;
  timestamp: string;
  pdf_page?: number;
  view_context?: unknown;
}

interface Session {
  id: string;
  title: string;
  last_activity_at: string;
  session_summary?: string;
  conversation_history?: unknown;
  title_source?: 'default' | 'auto' | 'manual';
  auto_title_attempts?: number;
  auto_title_generated_at?: string;
  total_duration_seconds?: number;
  current_session_start?: string;
  topic_id?: string;
  created_at?: string;
  updated_at?: string;
}

interface User {
  id: string;
  user_metadata?: {
    full_name?: string;
  } | null;
}

interface Topic {
  id: string;
  title: string;
}

export const useConversationManager = (
  session: Session | null | undefined,
  user: User | null,
  topic: Topic | null,
  topicId: string | undefined
) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Core conversation state
  const [messages, setMessages] = useState<Message[]>([]);
  const [unsavedMessageCount, setUnsavedMessageCount] = useState(0);
  const [currentSessionStartTime, setCurrentSessionStartTime] = useState<number | null>(null);
  const [accumulatedDurationSeconds, setAccumulatedDurationSeconds] = useState(0);
  
  // Guards to prevent duplicate end-session writes
  const endingInProgressRef = useRef(false);
  const lastEndAtRef = useRef(0);
  const lastEndedSessionIdRef = useRef<string | null>(null);
  
  // Auto-title management
  const autoTitleCooldownUntilRef = useRef(0);
  const lastAutoTitleAttemptRef = useRef(0);
  const autoTitleTriggeredSessionRef = useRef<string | null>(null);
  
  // Message refs for cleanup and persistence
  const messagesRef = useRef<Message[]>([]);
  const userRef = useRef<User | null>(null);

  // Keep refs in sync
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Utility functions
  const buildCombinedDeduped = useCallback((messages: Message[], viewingSessionMessages: Message[] = []) => {
    const allMessages = [...messages, ...viewingSessionMessages];
    const uniqueMap = new Map<string, Message>();
    
    allMessages.forEach(msg => {
      uniqueMap.set(msg.id, msg);
    });
    
    return Array.from(uniqueMap.values()).sort((a, b) => {
      const aTime = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : a.timestamp.getTime();
      const bTime = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : b.timestamp.getTime();
      return aTime - bTime;
    });
  }, []);

  const mapForSave = useCallback((messages: Message[]): PersistedMessage[] => {
    return messages.map(msg => ({
      id: msg.id,
      type: msg.type,
      content: msg.content,
      timestamp: typeof msg.timestamp === 'string' ? msg.timestamp : msg.timestamp.toISOString(),
      pdf_page: msg.pdf_page,
      view_context: msg.view_context
    }));
  }, []);

  const formatTimeAgo = useCallback((dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  }, []);

  // Load conversation history from database
  const loadConversationHistory = useCallback(async (sessionId: string) => {
    if (!user?.id) return [];

    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('conversation_history')
        .eq('id', sessionId)
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error loading conversation:', error);
        return [];
      }

      const history = (data?.conversation_history as any) || [];
      return history.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    } catch (error) {
      console.error('Error loading conversation:', error);
      return [];
    }
  }, [user?.id]);

  // Save current conversation to database
  const saveCurrentConversation = useCallback(async () => {
    if (!session?.id || !user?.id || messages.length === 0) return;

    try {
      const conversationData = mapForSave(messages);
      
      const { error } = await supabase
        .from('sessions')
        .update({
          conversation_history: conversationData as any,
          last_activity_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', session.id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error saving conversation:', error);
      } else {
        console.log(`💾 Saved ${messages.length} messages to session ${session.id}`);
        setUnsavedMessageCount(0);
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  }, [session?.id, user?.id, messages, mapForSave]);

  // Batch persistence trigger
  const persistConversationBatch = useCallback(async () => {
    console.log('📦 Batch save triggered');
    await saveCurrentConversation();
  }, [saveCurrentConversation]);

  // Auto-titling functionality
  const maybeAutoTitle = useCallback(async (sessionId: string, sessionMessages: Message[]) => {
    if (!user?.id || !sessionId) return;

    const now = Date.now();
    const cooldownDuration = 30000; // 30 seconds
    const minMessages = 4;

    // Check cooldown
    if (now < autoTitleCooldownUntilRef.current) {
      console.log('⏰ Auto-title on cooldown');
      return;
    }

    // Check if we have enough messages
    if (sessionMessages.length < minMessages) {
      console.log(`📝 Not enough messages for auto-title (${sessionMessages.length} < ${minMessages})`);
      return;
    }

    // Check if we already tried for this session recently
    if (autoTitleTriggeredSessionRef.current === sessionId) {
      console.log('🔄 Auto-title already attempted for this session');
      return;
    }

    try {
      autoTitleCooldownUntilRef.current = now + cooldownDuration;
      autoTitleTriggeredSessionRef.current = sessionId;
      lastAutoTitleAttemptRef.current = now;

      console.log('🤖 Attempting auto-title generation');
      
      // Get current session data to check attempts
      const { data: sessionData } = await supabase
        .from('sessions')
        .select('auto_title_attempts, title_source')
        .eq('id', sessionId)
        .single();

      if (!sessionData || sessionData.auto_title_attempts >= 3 || sessionData.title_source === 'manual') {
        console.log('❌ Session not eligible for auto-title');
        return;
      }

      // Increment attempts counter
      await supabase
        .from('sessions')
        .update({ auto_title_attempts: (sessionData.auto_title_attempts || 0) + 1 })
        .eq('id', sessionId);

      // Create conversation excerpt for title generation
      const recentMessages = sessionMessages.slice(-6);
      const conversationText = recentMessages
        .map(m => `${m.type === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
        .join('\n');


      // TODO: Implement actual title generation via AI
      console.log('📝 Would generate title for conversation:', conversationText.substring(0, 200));

    } catch (error) {
      console.error('Error in auto-title:', error);
    }
  }, [user?.id]);

  const scheduleAutoTitleIfEligible = useCallback((sessionMessages: Message[]) => {
    if (!session?.id) return;
    
    setTimeout(() => {
      maybeAutoTitle(session.id, sessionMessages);
    }, 2000);
  }, [session?.id, maybeAutoTitle]);

  // Message management
  const appendMessage = useCallback((message: Message) => {
    console.log('📩 Adding message:', message.type, message.content.substring(0, 50));
    
    setMessages(prev => {
      const updated = [...prev, message];
      scheduleAutoTitleIfEligible(updated);
      return updated;
    });
    
    setUnsavedMessageCount(prev => prev + 1);
  }, [scheduleAutoTitleIfEligible]);

  // Session duration management
  const startCurrentSession = useCallback(async () => {
    const now = Date.now();
    setCurrentSessionStartTime(now);
    console.log('▶️ Session started at', new Date(now).toISOString());
  }, []);

  const endCurrentSession = useCallback(async () => {
    if (!session?.id || !user?.id || !currentSessionStartTime) return;

    // Prevent duplicate end calls
    const now = Date.now();
    if (endingInProgressRef.current || 
        (now - lastEndAtRef.current < 1000) ||
        lastEndedSessionIdRef.current === session.id) {
      console.log('⏭️ Session end already in progress or recently ended');
      return;
    }

    endingInProgressRef.current = true;
    lastEndAtRef.current = now;
    lastEndedSessionIdRef.current = session.id;

    try {
      const sessionDuration = Math.floor((now - currentSessionStartTime) / 1000);
      console.log('⏹️ Ending session, duration:', sessionDuration, 'seconds');

      const { error } = await supabase
        .from('sessions')
        .update({
          current_session_start: null,
          total_duration_seconds: (session.total_duration_seconds || 0) + sessionDuration,
          last_activity_at: new Date().toISOString()
        })
        .eq('id', session.id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error ending session:', error);
      } else {
        console.log('✅ Session ended successfully');
        setAccumulatedDurationSeconds(prev => prev + sessionDuration);
        
        // Invalidate queries to refresh UI
        queryClient.invalidateQueries({ queryKey: ['topic-session', topicId] });
      }

      setCurrentSessionStartTime(null);
    } catch (error) {
      console.error('Error ending session:', error);
    } finally {
      endingInProgressRef.current = false;
    }
  }, [session?.id, user?.id, currentSessionStartTime, session?.total_duration_seconds, queryClient, topicId]);

  // Load messages when session changes
  useEffect(() => {
    if (!session?.id || !user?.id) {
      setMessages([]);
      return;
    }

    loadConversationHistory(session.id).then(loadedMessages => {
      console.log(`📚 Loaded ${loadedMessages.length} messages from session ${session.id}`);
      setMessages(loadedMessages);
      setUnsavedMessageCount(0);
    });
  }, [session?.id, user?.id, loadConversationHistory]);

  // Combined messages for display
  const combinedMessages = buildCombinedDeduped(messages);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Save any unsaved messages before unmounting
      if (messagesRef.current.length > 0 && session?.id && userRef.current?.id) {
        console.log('🔄 Component unmounting - saving conversation');
        saveCurrentConversation();
      }
    };
  }, [session?.id, saveCurrentConversation]);

  // Window unload handler
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (messagesRef.current.length > 0 && session?.id && userRef.current?.id) {
        console.log('🌅 Page unloading - saving conversation');
        // Synchronous save for page unload
        navigator.sendBeacon('/api/save-conversation', JSON.stringify({
          sessionId: session.id,
          userId: userRef.current.id,
          messages: mapForSave(messagesRef.current)
        }));
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [session?.id, mapForSave]);

  return {
    // State
    messages,
    setMessages,
    unsavedMessageCount,
    setUnsavedMessageCount,
    combinedMessages,
    currentSessionStartTime,
    accumulatedDurationSeconds,
    
    // Core Functions
    formatTimeAgo,
    loadConversationHistory,
    saveCurrentConversation,
    persistConversationBatch,
    
    // Session Management API
    appendMessage,
    startCurrentSession,
    endCurrentSession,
    scheduleAutoTitleIfEligible,
  };
};
